import { useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";

import { BANNER_LIST } from "@/lib/guidance/banners.config";
import { hasAnyChatThreads, isStageEligible, matchesRoute } from "@/lib/guidance/disclosure";
import type { TBannerDefinition } from "@/lib/guidance/types";
import { useGuidanceStage } from "@/hooks/useGuidanceStage";
import { useStore } from "@/store/store";

import { GuidanceBanner } from "./GuidanceBanner";

type TGuidanceBannerListProps = {
  pathname: string;
};

export function GuidanceBannerList({ pathname }: TGuidanceBannerListProps) {
  const navigate = useNavigate();
  const { stage, isGuidanceHydrated } = useGuidanceStage();
  const dismissedBanners = useStore((state) => state.guidanceState.banners);
  const companies = useStore((state) => state.companies);

  const visibleBanners = useMemo(() => {
    if (!isGuidanceHydrated) {
      return [] as TBannerDefinition[];
    }

    return BANNER_LIST.filter((banner) => {
      if (dismissedBanners[banner.id]) {
        return false;
      }
      if (!isStageEligible(stage, banner.requiredStage)) {
        return false;
      }
      if (!matchesRoute(pathname, banner.routes)) {
        return false;
      }
      if (banner.id === "next-step-chat-v1" && hasAnyChatThreads(companies)) {
        return false;
      }
      return true;
    });
  }, [companies, dismissedBanners, isGuidanceHydrated, pathname, stage]);

  const handleCta = (banner: TBannerDefinition) => {
    if (banner.id === "onboarding-reminder-v1") {
      const incompleteCompany = companies?.find((company) => !company.onboarding_completed);
      if (incompleteCompany) {
        void navigate({
          to: "/app/companies/$company_id/onboarding",
          params: { company_id: incompleteCompany.id },
        });
        return;
      }
      void navigate({ to: "/app/onboarding" });
    }
  };

  if (visibleBanners.length === 0) {
    return null;
  }

  return (
    <div className="px-4 pt-4 sm:px-6">
      {visibleBanners.map((banner) => (
        <GuidanceBanner
          key={banner.id}
          banner={banner}
          onCtaClick={banner.ctaLabel ? () => handleCta(banner) : undefined}
        />
      ))}
    </div>
  );
}
